import { format, parseISO } from 'date-fns'
import { CalendarClock, CalendarDays, Check, ListChecks } from 'lucide-react'
import { EgressIpMapping } from '@/components/layout/oci-migration-notice/egress-ip-mapping'
import { EndpointMigrationList } from '@/components/layout/oci-migration-notice/endpoint-migration-list'
import {
  MigrationNotes,
  MigrationSchedule,
} from '@/components/layout/oci-migration-notice/migration-schedule'
import { Muted } from '@/components/sv/typographies'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { LEGACY_ENDPOINT_RETIREMENT_DATE } from '@/config/legacy-endpoints'
import { useCompanyPath } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type { MigratingService } from '@/components/layout/oci-migration-notice/endpoint-migration-list'
import type { SvlOciMigrationQuery } from '@/components/layout/oci-migration-notice/queries.types.gen'
import type { ReactNode } from 'react'

type Migration = NonNullable<SvlOciMigrationQuery['svlOciMigration']>

type WaveStepStatus = 'completed' | 'current' | 'upcoming'

type WaveStepProps = {
  value: string
  title: string
  subtitle: string
  status: WaveStepStatus
  isLast?: boolean
  children: ReactNode
}

const WaveStep = ({
  value,
  title,
  subtitle,
  status,
  isLast = false,
  children,
}: WaveStepProps) => {
  const isCompleted = status === 'completed'
  const isCurrent = status === 'current'
  const isUpcoming = status === 'upcoming'

  return (
    <AccordionItem value={value} className="flex gap-2 border-0">
      <div className="flex flex-col items-center pt-3.5">
        <div
          aria-hidden="true"
          className={cn(
            'flex size-6 shrink-0 items-center justify-center rounded-full border bg-background',
            isCompleted && 'border-success bg-success text-white',
            isCurrent && 'border-2 border-success',
            isUpcoming && 'border-border bg-muted'
          )}
        >
          {isCompleted ? <Check className="size-3.5" /> : null}
          {isCurrent ? (
            <span className="size-2 rounded-full bg-success" />
          ) : null}
        </div>
        {isLast ? null : (
          <div
            aria-hidden="true"
            className={cn(
              'my-1 w-px flex-1',
              isCompleted ? 'bg-success' : 'bg-border'
            )}
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <AccordionTrigger
          className={cn(
            'items-center px-1 py-3 hover:no-underline',
            isUpcoming && 'text-muted-foreground'
          )}
        >
          <div className="flex flex-col gap-0.5">
            <span className="flex items-center gap-2">
              {title}
              {isCurrent ? <Badge variant="secondary">Your wave</Badge> : null}
              {isCompleted ? (
                <Badge variant="outline" className="text-muted-foreground">
                  Completed
                </Badge>
              ) : null}
            </span>
            <span className="text-xs font-normal text-muted-foreground">
              {subtitle}
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-4 px-1 pb-4">
          {children}
        </AccordionContent>
      </div>
    </AccordionItem>
  )
}

type WaveEntryProps = {
  title: string
  icon?: ReactNode
  children: ReactNode
}

const WaveEntry = ({ title, icon, children }: WaveEntryProps) => (
  <div className="flex flex-col gap-2">
    <span className="flex items-center gap-2 font-medium">
      {icon}
      {title}
    </span>
    {children}
  </div>
)

const TaskNumber = ({ number }: { number: number }) => (
  <span
    aria-hidden="true"
    className="shrink-0 font-mono text-xs text-muted-foreground"
  >
    #{number}
  </span>
)

const DeadlineBadge = () => (
  <Badge
    variant="outline"
    className="shrink-0 gap-1 border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-50"
  >
    <CalendarClock aria-hidden="true" />
    Due{' '}
    <time dateTime={LEGACY_ENDPOINT_RETIREMENT_DATE.iso}>
      {format(parseISO(LEGACY_ENDPOINT_RETIREMENT_DATE.iso), 'MMM d')}
    </time>
  </Badge>
)

type WaveTask = {
  id: string
  title: string
  hasDeadline?: boolean
  content: ReactNode
}

const formatEndpoint = (
  hostname: string | null | undefined,
  port: string | number | null | undefined
) => (hostname && port ? `${hostname}:${port}` : null)

type WaveDetailsProps = Pick<
  Migration,
  'gcpClusters' | 'tcpProxyApps' | 'externalDatabases'
> & {
  waveLabel: string
  isShowingExternalDatabases: boolean
  onNavigate: () => void
}

const WaveDetails = ({
  waveLabel,
  gcpClusters,
  tcpProxyApps,
  externalDatabases,
  isShowingExternalDatabases,
  onNavigate,
}: WaveDetailsProps) => {
  const { getCompanyPath } = useCompanyPath()

  const tcpProxyServices: MigratingService[] = tcpProxyApps.map(
    ({ id, app, tcpProxies }) => ({
      id,
      name: app.displayName,
      href: getCompanyPath({
        pathname: '/app/:appName/networking',
        variables: { appName: app.name },
      }),
      endpoints: tcpProxies.flatMap((proxy) => {
        const legacyEndpoint = formatEndpoint(
          proxy.hostname,
          proxy.externalPort
        )
        if (!legacyEndpoint) {
          return []
        }
        return [
          {
            id: proxy.id,
            label: `Port ${proxy.port}`,
            legacyEndpoint,
            nextEndpoint: formatEndpoint(
              proxy.externalProxyHostname,
              proxy.externalProxyPort
            ),
          },
        ]
      }),
    })
  )

  const databaseServices: MigratingService[] = externalDatabases.flatMap(
    (database) => {
      const legacyEndpoint = formatEndpoint(
        database.externalHostname,
        database.externalPort
      )
      if (!legacyEndpoint) {
        return []
      }
      return [
        {
          id: database.id,
          name: database.displayName,
          href: getCompanyPath({
            pathname: '/database/:databaseName/networking',
            variables: { databaseName: database.name },
          }),
          endpoints: [
            {
              id: database.id,
              legacyEndpoint,
              nextEndpoint: database.isExternalProxyEnabled
                ? formatEndpoint(
                    database.externalProxyHostname,
                    database.externalProxyPort
                  )
                : null,
            },
          ],
        },
      ]
    }
  )

  const hasTcpProxies = tcpProxyServices.some(
    (service) => service.endpoints.length > 0
  )
  const hasExternalDatabases =
    isShowingExternalDatabases && databaseServices.length > 0

  const tasks: WaveTask[] = [
    {
      id: 'egress-ips',
      title:
        'Prepare your third-party services for the new egress IP addresses',
      content: (
        <>
          <Muted as="p" className="text-xs">
            Outbound traffic from your applications will originate from new IP
            addresses once they run on Oracle Cloud. If you allowlist Sevalla's
            egress IPs on external services such as MongoDB Atlas, payment
            providers or private APIs, add the addresses below now so nothing
            breaks during the migration.
          </Muted>
          <EgressIpMapping gcpClusters={gcpClusters} />
        </>
      ),
    },
    ...(hasTcpProxies
      ? [
          {
            id: 'tcp-proxies',
            title: 'Switch your TCP proxies to the new endpoints',
            hasDeadline: true,
            content: (
              <>
                <Muted as="p" className="text-xs">
                  TCP proxies are moving from the legacy gateway endpoint to a
                  new external proxy endpoint that keeps working after the
                  migration. Update every client that connects through these
                  proxies to the new hostname and port. The legacy endpoint will
                  stop working on {LEGACY_ENDPOINT_RETIREMENT_DATE.label}.
                </Muted>
                <EndpointMigrationList
                  services={tcpProxyServices}
                  onNavigate={onNavigate}
                />
              </>
            ),
          },
        ]
      : []),
    ...(hasExternalDatabases
      ? [
          {
            id: 'external-databases',
            title:
              'Switch your database external connections to the new endpoints',
            hasDeadline: true,
            content: (
              <>
                <Muted as="p" className="text-xs">
                  External database connections are moving from the legacy
                  gateway endpoint to a new external proxy endpoint. Update
                  every client that connects to these databases from outside
                  Sevalla to the new hostname and port. If the new endpoint
                  isn't available yet, open the database's networking page to
                  enable it. The legacy endpoint will stop working on{' '}
                  {LEGACY_ENDPOINT_RETIREMENT_DATE.label}.
                </Muted>
                <EndpointMigrationList
                  services={databaseServices}
                  onNavigate={onNavigate}
                />
              </>
            ),
          },
        ]
      : []),
  ]

  const tasksTitle = tasks.length === 1 ? 'Task' : 'Tasks'

  return (
    <>
      <WaveEntry
        title="Schedule"
        icon={
          <CalendarDays
            aria-hidden="true"
            className="size-4 shrink-0 text-muted-foreground"
          />
        }
      >
        <MigrationSchedule waveLabel={waveLabel} />
      </WaveEntry>
      <WaveEntry
        title={tasksTitle}
        icon={
          <ListChecks
            aria-hidden="true"
            className="size-4 shrink-0 text-muted-foreground"
          />
        }
      >
        <Accordion type="multiple" className="flex flex-col gap-2">
          {tasks.map((task, index) => (
            <AccordionItem
              key={task.id}
              value={task.id}
              className="rounded-lg border border-border px-3 last:border-b"
            >
              <AccordionTrigger className="items-center gap-3 py-2.5 text-sm hover:no-underline">
                <span className="flex flex-1 flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
                  <span className="flex items-center gap-2">
                    <TaskNumber number={index + 1} />
                    {task.title}
                  </span>
                  {task.hasDeadline ? <DeadlineBadge /> : null}
                </span>
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-3 pb-3">
                {task.content}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </WaveEntry>
      <MigrationNotes waveLabel={waveLabel} />
    </>
  )
}

type WaveTimelineProps = {
  migration: Migration
  onNavigate: () => void
}

export const WaveTimeline = ({ migration, onNavigate }: WaveTimelineProps) => {
  const { wave, gcpClusters, tcpProxyApps, externalDatabases } = migration
  const isWave1 = wave === 'wave1'
  const isWave2 = wave === 'wave2'

  return (
    <Accordion type="single" collapsible defaultValue={wave ?? undefined}>
      <WaveStep
        value="wave0"
        title="Wave 0"
        subtitle="Static site builds"
        status="completed"
      >
        <Muted as="p" className="text-xs">
          Static site builds for all customers already run in Oracle Cloud's US
          East (Ashburn) data center. Nothing changed for your sites and no
          action was needed.
        </Muted>
      </WaveStep>
      <WaveStep
        value="wave1"
        title="Wave 1"
        subtitle="Accounts with stateless services only"
        status={isWave1 ? 'current' : 'upcoming'}
      >
        {isWave1 ? (
          <WaveDetails
            waveLabel="Wave 1"
            gcpClusters={gcpClusters}
            tcpProxyApps={tcpProxyApps}
            externalDatabases={externalDatabases}
            isShowingExternalDatabases={false}
            onNavigate={onNavigate}
          />
        ) : (
          <Muted as="p" className="text-xs">
            Wave 1 covers accounts that only run static sites and applications
            without persistent storage. Because your account uses stateful
            services, it is scheduled for Wave 2.
          </Muted>
        )}
      </WaveStep>
      <WaveStep
        value="wave2"
        title="Wave 2"
        subtitle="Accounts with databases or persistent storage"
        status={isWave2 ? 'current' : 'upcoming'}
        isLast
      >
        {isWave2 ? (
          <>
            <WaveDetails
              waveLabel="Wave 2"
              gcpClusters={gcpClusters}
              tcpProxyApps={tcpProxyApps}
              externalDatabases={externalDatabases}
              isShowingExternalDatabases
              onNavigate={onNavigate}
            />
            <Muted as="p" className="text-xs">
              We'll add more tasks and details here as Wave 2 approaches, so
              check back before your migration starts.
            </Muted>
          </>
        ) : (
          <Muted as="p" className="text-xs">
            Wave 2 covers accounts with managed databases or applications with
            persistent disks. Your account doesn't use stateful services, so it
            is scheduled for Wave 1.
          </Muted>
        )}
      </WaveStep>
    </Accordion>
  )
}
